import React, { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import Spinner from '@/components/ui/Spinner';
import Import from '@/modules/Administration/components/settings/Import';

export default function DataTransfersSettings({ currentCompany }) {
    const [transfers, setTransfers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showImportModal, setShowImportModal] = useState(false);

    useEffect(() => {
        if (!currentCompany?.id) return;
        
        const fetchTransfers = async () => {
            setIsLoading(true);
            try {
                const q = query(
                    collection(db, 'companies', currentCompany.id, 'data_transfers'),
                    orderBy('createdAt', 'desc'),
                    limit(100)
                );
                const snap = await getDocs(q);
                const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setTransfers(data);
            } catch {
                // Ignore error silently
            } finally {
                setIsLoading(false);
            }
        };
        fetchTransfers();
    }, [currentCompany?.id]);

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="animate-fade-in" style={{ padding: '32px 0', maxWidth: '1000px' }}>
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111', marginBottom: '16px' }}>Import and Export</h1>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '13px', color: '#666' }}>Learn more about import and export</div>
                    <a href="#" style={{ fontSize: '13px', color: '#0052cc', textDecoration: 'none', fontWeight: '500' }}>Set up your import files <ObliqueIcon name="open_in_new" size={12} /></a>
                    <a href="#" style={{ fontSize: '13px', color: '#0052cc', textDecoration: 'none', fontWeight: '500' }}>Export your records <ObliqueIcon name="open_in_new" size={12} /></a>
                </div>
            </div>

            {/* Import Data Section */}
            <div style={{ background: '#fff', border: '1px solid #eaeaea', borderRadius: '8px', padding: '24px', marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111', margin: '0 0 4px 0' }}>Import data</h2>
                    <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Import files or Salesforce data</p>
                </div>
                <button 
                    onClick={() => setShowImportModal(true)}
                    style={{ background: '#fff', border: '1px solid #111', color: '#111', padding: '8px 16px', borderRadius: '4px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
                >
                    Go to import
                </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111', margin: '0 0 8px 0' }}>Transfer Audit</h2>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>This is an audit of imports and exports from your Oblique account. You can download exports that are less than 30 days old. <a href="#" style={{ color: '#0052cc', textDecoration: 'none' }}>Learn more</a></p>
            </div>

            <div style={{ background: '#fff', border: '1px solid #eaeaea', borderRadius: '8px', overflow: 'hidden' }}>
                {/* Table Header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', padding: '16px 24px', background: '#fafafa', borderBottom: '1px solid #eaeaea', fontSize: '13px', fontWeight: '600', color: '#111' }}>
                    <div>Name</div>
                    <div>Source</div>
                    <div>Exported/Imported records</div>
                    <div>User</div>
                    <div>Date</div>
                </div>

                {/* Table Body */}
                <div style={{ minHeight: '200px' }}>
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <Spinner size="md" />
                        </div>
                    ) : transfers.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#999' }}>
                            <ObliqueIcon name="sync_disabled" size={32} style={{ marginBottom: '12px' }} />
                            <div style={{ fontSize: '14px' }}>No data transfers found.</div>
                        </div>
                    ) : (
                        transfers.map(t => (
                            <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', padding: '16px 24px', borderBottom: '1px solid #eaeaea', alignItems: 'center', fontSize: '14px', transition: 'background 0.2s' }} className="hover-bg-subtle">
                                
                                {/* Name */}
                                <div>
                                    <div style={{ color: '#111', fontWeight: '500', marginBottom: '4px' }}>{t.module || 'System Transfer'}</div>
                                    <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#999' }} />
                                        Download expired <span style={{ color: '#0052cc', cursor: 'pointer', fontWeight: '500' }}>View history</span>
                                    </div>
                                </div>
                                
                                {/* Source */}
                                <div style={{ color: '#111' }}>
                                    {t.type === 'IMPORT' ? 'Import UI' : 'Export UI'}
                                </div>
                                
                                {/* Records */}
                                <div style={{ color: '#111' }}>
                                    {t.records || 0}
                                </div>
                                
                                {/* User */}
                                <div style={{ color: '#111', display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: '500' }}>{t.createdBy?.split('@')[0] || 'System'}</span>
                                    <span style={{ fontSize: '12px', color: '#666' }}>{t.createdBy || 'System User'}</span>
                                </div>

                                {/* Date */}
                                <div style={{ color: '#111', fontSize: '13px', display: 'flex', flexDirection: 'column' }}>
                                    <span>{formatDate(t.createdAt).split(',')[0]}</span>
                                    <span style={{ color: '#666' }}>{formatDate(t.createdAt).split(',')[1]}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <Import 
                isOpen={showImportModal} 
                onClose={() => setShowImportModal(false)} 
                companyId={currentCompany?.id} 
                targetCollection="deal_audits" 
            />
        </div>
    );
}
